import type {ReactElement} from 'react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {GRID_ITEM_TYPE, GridItemProps} from '@Components/base-grid/components/grid-item';
import {GridItem} from '@Components/base-grid/components/grid-item';
import {InputField} from '@Components/input-field';
import {toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {addChangeToLog} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-reducer';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import {InfoTab, ManagePMWStockPanelType, PMWStockItemType} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import {isSafari} from 'react-device-detect';
import {useIsInViewport} from '@Hooks/useIsInViewport';
import {Text, TextSize} from '@Components/text';
import {arraysEqual} from '@Utils/array.util';
import {GridShapeThumb} from '@Components/base-grid/components/grid-shape-thumb/grid-shape-thumb';
import {MediaHoverPreview} from '@Components/media-hover-preview/media-hover-preview';
import type {GridMediaItemStorage} from '../grid-media-item';
import styles from './grid-pmwstockmedia-item.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export interface GridPMWStockID {
  idPMmwStockMedia: string;
  idPMWStockAnimatedSticker?: string;
  idPMWStockShape?: string;
  idPMWStockStillSticker?: string;
  idPMWStockAiAnimatedSticker?: string;
  idPMWStockAiSticker?: string;
  idPMWExtractedGettySticker?: string;
  idPMWStockIcon?: string;
  idPMWStockAiIcon?: string;
  idPMWStockEmoji?: string;
  idPMWStockImage?: string;
}

export interface GridPMWStockMediaItemStorage extends GridMediaItemStorage {
  type: GRID_ITEM_TYPE.PMW_STOCK_MEDIA;
  hashedFilename?: string;
  isHidden: number;
  keywords: string[];
  description: string;
  stockType: string;
  isEdited: boolean;
  isMask?: number;
  isQrLogoSuggestion?: number;
  idPMWStock: GridPMWStockID;
  adminScore?: number;
  scrollToItem: boolean;
  iconCategory?: string;
  shapeCategory?: string;
}

interface GridPMWStockMediaItemProps extends GridItemProps {
  stockType: PMWStockItemType;
  thumbSrc: string;
  paddedThumb?: boolean;
  keywords: string[];
  isEdited: boolean;
  isDeleted?: boolean;
  scrollToItem: boolean;
  id: string;
  adminScore: number;
  iconCategory?: string;
  shapeCategory?: string;
  description: string;
}

export function GridPMWStockMediaItem({isEdited, isDeleted = false, style, ...props}: GridPMWStockMediaItemProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const gridReduxState = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId ?? ''];
  });
  const changesReduxState = useAppSelector((state) => {
    return state.ManagePMWStockPanel.PMWStockChanges;
  });

  const selectedGridItemIds = useAppSelector((state) => {
    return state.grids.gridSelectionGroupHashmap[props.gridId ?? ''].selectedItemIdHashmap;
  });

  const gridDefaultInfoTab = useAppSelector((state) => {
    return state.ManagePMWStockPanel.gridDefaultInfoTab;
  });

  const [gridItem, setGridItem] = useState<HTMLDivElement | null>(null);
  const isInViewPort = useIsInViewport(gridItem as HTMLElement);
  const [, setIsStickerBackupImageLoaded] = useState(false);
  const [, setIsShapeImageLoaded] = useState(false);

  const gridItemRef = useRef<HTMLDivElement | null>(null);

  const [focusOnKeywordContainer, setFocusOnKeywordContainer] = useState(false);
  const [itemInfoTab, setItemInfoTab] = useState<InfoTab>(gridDefaultInfoTab);

  useEffect(() => {
    setItemInfoTab(gridDefaultInfoTab);
  }, [gridDefaultInfoTab]);

  useEffect(() => {
    if (props.scrollToItem && gridItemRef.current) {
      gridItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    if (props.scrollToItem && props.gridId && !(props.id in selectedGridItemIds)) {
      dispatch(
        updateGridItem({
          gridId: props.gridId,
          itemId: props.id,
          changes: {scrollToItem: false},
        })
      );
    }
  }, [gridReduxState, selectedGridItemIds]);

  const handleOnChange = useCallback(
    (e: string, id: string): void => {
      const gridItems = gridReduxState.items as GridPMWStockMediaItemStorage[];
      const itemToChange = gridItems.find((item: GridPMWStockMediaItemStorage): boolean => {
        return item.id === id;
      });

      if (itemToChange === undefined) {
        return;
      }

      let changes: Partial<GridPMWStockMediaItemStorage>;

      if (itemInfoTab === InfoTab.DESCRIPTION) {
        const updatedDescription = e.trim();

        changes = {description: updatedDescription};
      } else {
        let updatedKeywords = e.split(',').map((keyword) => {
          return keyword.trim().toLowerCase();
        });

        if (updatedKeywords.length === 1 && updatedKeywords[0] === '') {
          updatedKeywords = [];
        } else {
          updatedKeywords = Array.from(new Set(updatedKeywords));
        }

        changes = {keywords: updatedKeywords};
      }

      let changedItem = {...itemToChange, ...changes} as Partial<GridPMWStockMediaItemStorage>;
      let edited = true;

      if (itemInfoTab === InfoTab.KEYWORDS) {
        if (arraysEqual(itemToChange.keywords, changedItem.keywords ?? [])) {
          edited = false;
        }
      } else {
        if (itemToChange.description === changedItem.description) {
          edited = false;
        }
      }

      if (changesReduxState[itemToChange.id]?.original) {
        edited = true;
        const originalItem = omit(changesReduxState[itemToChange.id].original, 'isEdited') as Partial<GridPMWStockMediaItemStorage>;
        changedItem = omit(changedItem, 'isEdited');

        if (isEqual(originalItem, changedItem)) {
          edited = false;
        }
      }

      const changedItemToLog = {id: changedItem.id, ...changes, isEdited: edited} as Partial<GridPMWStockMediaItemStorage>;
      dispatch(addChangeToLog({changeToLog: changedItemToLog, itemToChange}));
      dispatch(
        updateGridItem({
          gridId: props.gridId ?? '',
          itemId: itemToChange.id,
          changes: {...changes, isEdited: edited},
        })
      );
      dispatch(
        updateGridItem({
          gridId: ManagePMWStockPanelType.PMW_ALL_STOCK,
          itemId: itemToChange.id,
          changes: {...changes, isEdited: edited},
        })
      );
    },
    [dispatch, gridReduxState.items, changesReduxState, props.gridId, itemInfoTab]
  );

  const getInputContainer = (): ReactElement<any> => {
    const isItemSelected = props.id in selectedGridItemIds;

    return (
      <div className={`border-s-standard flex-vertical-center ${styles.keywordsContainer}`}>
        <div className={isItemSelected ? styles.itemOverlay : ''} />
        <InputField
          onSelect={(): void => {
            setFocusOnKeywordContainer(true);
          }}
          onClick={(e): void => {
            if (!isItemSelected) {
              e.stopPropagation();
            } else {
              dispatch(
                toggleGridItemSelection({
                  gridItemId: props.id,
                  gridSelectionGroupId: props.gridSelectionGroupId ?? '',
                  select: true,
                })
              );
            }
          }}
          onInputBlur={(e: string): void => {
            setFocusOnKeywordContainer(!focusOnKeywordContainer);
            handleOnChange(e, props.id);
          }}
          multiLineRows={2}
          inputClassName={styles.textArea}
          showInputIcon={false}
          isMultiLine
          className={`_full-width body-s ${focusOnKeywordContainer ? styles.textAreaFocussed : styles.textArea}`}
          value={itemInfoTab === InfoTab.KEYWORDS ? props.keywords.join(', ') : props.description}
          maxLength={500}
        />
      </div>
    );
  };

  const getStickerThumbElement = (isInViewport: boolean): ReactElement<any> => {
    if (isSafari) {
      return (
        <img
          onLoad={(): void => {
            setIsStickerBackupImageLoaded(true);
          }}
          className={`flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`}
          draggable="false"
          src={isInViewport ? props.thumbSrc : ''}
          alt={window.i18next.t('pmwjs_sticker')}
        />
      );
    }
    return (
      <img
        className={`flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`}
        draggable="false"
        onLoad={(): void => {
          setIsStickerBackupImageLoaded(true);
        }}
        src={isInViewport ? props.thumbSrc : ''}
        alt={window.i18next.t('pmwjs_sticker')}
      />
    );
  };

  const getShapeThumbElement = (): ReactElement<any> => {
    const thumbClassName = `flex-v-row flex-center radius-12 ${focusOnKeywordContainer ? styles.thumbItemShrinked : styles.thumbItem}`;
    if (props.stockType === PMWStockItemType.SHAPE) {
      return <GridShapeThumb thumbSrc={props.thumbSrc} className={thumbClassName} />;
    }
    return (
      <img
        onLoad={(): void => {
          setIsShapeImageLoaded(true);
        }}
        src={props.thumbSrc}
        className={thumbClassName}
        alt={window.i18next.t('pmwjs_image')}
        draggable="false"
        loading="lazy"
      />
    );
  };

  const getShapeHoverPreviewContent = (): ReactElement => {
    return <GridShapeThumb thumbSrc={props.thumbSrc} className="_full-width _full-height" />;
  };

  const applyItemClassName = (isItemEdited: boolean, isItemDeleted: boolean): string => {
    if (isItemDeleted) {
      return styles.deletedItem;
    }
    if (isItemEdited) {
      return styles.editedItem;
    }
    return styles.ItemDefaultStyle;
  };

  return (
    <GridItem {...props} style={style} containerClassName={applyItemClassName(isEdited, isDeleted)}>
      {props.stockType === PMWStockItemType.ANIMATED_STICKER ? (
        <div
          ref={(element): void => {
            setGridItem(element);
            gridItemRef.current = element;
          }}
          className={`flex-center spacing-m-l-1 ${focusOnKeywordContainer ? styles.thumbContainerFocussed : styles.thumbContainer}`}
        >
          <MediaHoverPreview previewSrc={props.thumbSrc} isEnabled={!focusOnKeywordContainer && !isDeleted} className="flex-center _full-width _full-height">
            {React.cloneElement(getStickerThumbElement(isInViewPort))}
          </MediaHoverPreview>
        </div>
      ) : (
        <div className={`flex-center spacing-m-l-1 ${focusOnKeywordContainer ? styles.thumbContainerFocussed : ''} ${styles.thumbContainer}`} ref={gridItemRef}>
          <MediaHoverPreview
            previewSrc={props.thumbSrc}
            isEnabled={!focusOnKeywordContainer && !isDeleted}
            getPreviewContent={props.stockType === PMWStockItemType.SHAPE ? getShapeHoverPreviewContent : undefined}
            className="flex-center _full-width _full-height"
          >
            {getShapeThumbElement()}
          </MediaHoverPreview>
        </div>
      )}
      <div className={styles.infoContainer}>
        {!focusOnKeywordContainer ? (
          <div className="flex-row">
            <Text val={`Score: ${props.adminScore}`} size={TextSize.XXSMALL} className={styles.adminScore} />
            <Text
              val="Keywords"
              size={TextSize.XXSMALL}
              className={`_cursor-pointer ${itemInfoTab === InfoTab.KEYWORDS ? styles.tabLinkActive : ''}`}
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                setItemInfoTab(InfoTab.KEYWORDS);
              }}
            />
            <Text
              val="Description"
              size={TextSize.XXSMALL}
              className={`spacing-m-l-2 _cursor-pointer ${itemInfoTab === InfoTab.DESCRIPTION ? styles.tabLinkActive : ''}`}
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                setItemInfoTab(InfoTab.DESCRIPTION);
              }}
            />
          </div>
        ) : null}

        {getInputContainer()}
      </div>
    </GridItem>
  );
}
